import type { SxProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

/**
 * Visible focus indicator for any element made operable by `clickableProps`
 * from `./interactive`. That helper puts plain text, artwork and cards into the
 * tab order, but a `div` gets no focus styling of its own — so without this a
 * keyboard user can reach a target with no way to tell which one they are on.
 * Every hover affordance in the feature needs a focus counterpart.
 *
 * No explicit radius: `outline` follows the element's own `border-radius`, so
 * cards and squircle game icons get a correctly rounded ring for free.
 *
 * @param offset Distance from the element's edge. Pass a negative value for a
 * target that sits flush inside a bordered container (a table row or chart
 * strip), where an outset ring would be clipped by the parent.
 */
export const focusRingSx = (offset: number = 2): SystemStyleObject<Theme> => ({
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: offset,
  },
})

/**
 * Typography styled as a primary-coloured clickable link (cursor + hover underline).
 * Used by every "click to drill down" row label across tables and info strips.
 *
 * Pair with `clickableProps` from `./interactive`, which supplies the button
 * role and keyboard handling the focus ring is there to reveal.
 */
export const linkTypographySx: SxProps<Theme> = {
  cursor: 'pointer',
  color: 'primary.main',
  '&:hover': { textDecoration: 'underline' },
  ...focusRingSx(),
}

/**
 * Same as `linkTypographySx` but with a bolder weight, used inside `GRTable` cells
 * where the clickable label is the row identifier.
 */
export const linkTypographyBoldSx: SxProps<Theme> = {
  ...linkTypographySx,
  fontWeight: 'bold',
}

/**
 * `sx` applied to a MUI `Chip` that has a click handler, giving it a subtle hover state.
 * Callers still need to pass `clickable` / `onClick`; this only supplies the visual cue.
 */
export const clickableChipSx: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'action.hover' },
}

/**
 * Corner radius for game artwork. Matches the shared `GameIcon` component (see
 * `GameIcon.scss`) so icons render as the same iOS-style "squircle" the rest of
 * the app uses in lists, dialogs, and chart hovercards.
 */
export const GAME_ICON_RADIUS = '22%'

/**
 * Base styles for a game icon at a given pixel size. Returns a plain object so
 * callers needing extras (hover transitions, drop shadows) can spread it:
 * `sx={{ ...gameIconSx(38), boxShadow: 2 }}`.
 *
 * @param size Width and height in pixels.
 * @param options `border` and `background` default to on, which is the form
 * used wherever an icon sits on a plain surface and needs to read as artwork
 * even before the image loads.
 */
export const gameIconSx = (size: number, options?: { border?: boolean; background?: boolean }): SystemStyleObject<Theme> => ({
  width: size,
  height: size,
  borderRadius: GAME_ICON_RADIUS,
  objectFit: 'cover',
  flexShrink: 0,
  ...(options?.background === false ? {} : { backgroundColor: 'action.hover' }),
  ...(options?.border === false ? {} : { border: '1px solid rgba(0, 0, 0, 0.1)' }),
})

/**
 * `actionsSx` for the drilldown dialogs whose footer holds a single centred
 * call-to-action (the motivation Blind Spot / Alignment / Shared / Diverging
 * dialogs).
 */
export const centredDialogActionsSx: SxProps<Theme> = { px: 3, py: 2, justifyContent: 'center', gap: 1.5 }
